import React from 'react'

type Heading = { id: string; text: string; level: number }

/** GitHub-style slug so in-page anchors match the wiki's own TOC links. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
}

/**
 * "On this page" rail: reads the h2/h3 headings from the rendered content, assigns
 * anchor ids, and highlights the section in view (scroll-spy). Hidden when there's little to show.
 */
export default function OnThisPage({ containerRef, watch }: { containerRef: React.RefObject<HTMLElement>; watch: unknown }) {
  const [headings, setHeadings] = React.useState<Heading[]>([])
  const [active, setActive] = React.useState<string>('')

  React.useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll('h2, h3')) as HTMLElement[]
    const list = els.map((el) => {
      if (!el.id) el.id = slugify(el.textContent || '')
      return { id: el.id, text: el.textContent || '', level: el.tagName === 'H3' ? 3 : 2 }
    })
    setHeadings(list)

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive((visible[0].target as HTMLElement).id)
      },
      { rootMargin: '-120px 0px -72% 0px' },
    )
    els.forEach((el) => observer.observe(el))

    // At the bottom of the page the last heading never enters the active band, so pin it explicitly.
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
      if (atBottom && list.length) setActive(list[list.length - 1].id)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [containerRef, watch])

  if (headings.length < 2) return null

  return (
    <nav className="on-this-page" aria-label="On this page">
      <div className="otp-title">On this page</div>
      <ul className="otp-list">
        {headings.map((h) => (
          <li key={h.id} className={`otp-item otp-l${h.level} ${active === h.id ? 'is-active' : ''}`}>
            <a href={`#${h.id}`}>{h.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
